import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as LessIcon } from 'assets/icons/less.svg';
import { formatCurrency } from 'utils/formatCurrency';
import Translate from 'components/Translate';
import useLiterals from 'utils/hooks/useLiterals';
import InputAmount from '../InputAmount';

const InputAmountSteps = (props) => {
  const {
    value = 0,
    originalValue = 0,
    min = 0,
    max = 0,
    step = 20,
    decimals = true,
    error = false,
    onChange = () => {},
    clampOnBlur = true,
  } = props;

  const literals = useLiterals('common');

  const minAmount = useMemo(() => (
    originalValue ? Math.min(min, originalValue) : min
  ), [min, originalValue]);

  const disabledLess = !min || value <= minAmount;
  const disabledMore = !max || value >= max;

  const handlePaymentControl = (amountStep) => {
    let newAmount = parseFloat(value + amountStep) || null;
    if (newAmount !== null) {
      if (max && amountStep > 0 && newAmount > max) {
        newAmount = max;
      } else if (minAmount && amountStep < 0 && newAmount < minAmount) {
        newAmount = minAmount;
      }
      onChange(parseFloat(newAmount.toFixed(2)));
    } else {
      onChange(null);
    }
  };

  const handleOnChange = (v) => {
    if (v === undefined || v === '' || v === null) {
      onChange(null);
      return;
    }
    const parsed = parseFloat(v);
    if (clampOnBlur) {
      onChange(Number.isNaN(parsed) ? 0 : parsed);
      return;
    }
    onChange(Number.isNaN(parsed) ? null : parsed);
  };

  return (
    <>
      <div className='flex gap-2'>
        <div
          className={`payment-input flex items-center justify-center ${disabledLess ? 'disabled' : ''}`}
          onClick={() => !disabledLess && handlePaymentControl(-step)}
        >
          <LessIcon />
        </div>
        <InputAmount
          className='w-full'
          placeholder={literals.insertAmount}
          value={value}
          min={minAmount}
          max={max}
          decimals={decimals}
          disabled={!max}
          clampOnBlur={clampOnBlur}
          onChange={handleOnChange}
        />
        <div
          className={`payment-input flex items-center justify-center ${disabledMore ? 'disabled' : ''}`}
          onClick={() => !disabledMore && handlePaymentControl(+step)}
        >
          <PlusIcon />
        </div>
      </div>
      {error && (
        <Translate
          component='div'
          className='payment-input-error pt-2'
          literal={originalValue ? literals.amountRangeErrorWithOriginal : literals.amountRangeError}
          vars={{
            amount: formatCurrency(originalValue),
            min: formatCurrency(min),
            max: formatCurrency(max),
          }}
        />
      )}
    </>
  );
};

InputAmountSteps.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  originalValue: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  decimals: PropTypes.bool,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  clampOnBlur: PropTypes.bool,
};

export default InputAmountSteps;
